<script setup lang="ts">
interface Props {
  quickFiltersKey?: string
}

const props = withDefaults(defineProps<Props>(), {
  quickFiltersKey: 'pattern',
})

const filterStore = useFilterStore()
const { $t } = useNuxtApp()

const { filterMenuState } = storeToRefs(filterStore)

const quickFilterItems = computed(() => filterMenuState.value.find(({ id }) => id === props.quickFiltersKey)?.items.filter(({ disabled }) => !disabled))

function applyFacet(id: string, itemId: string) {
  const clickedFilterItem = quickFilterItems.value?.find(element => element.id === itemId)
  const isAlreadySelected = Boolean(clickedFilterItem?.value)
  filterStore.clearFilterGroup(id)

  if (isAlreadySelected) {
    filterStore.sendFilterClickEvent(id, 'quickFilter')
    filterStore.updateRouteQueryFromState()
    return
  }

  filterStore.applyFilterItem(id, itemId, true)
  filterStore.sendFilterClickEvent(id, 'quickFilter')
  filterStore.updateRouteQueryFromState()
}
</script>

<template>
  <div
    class="quick-filters"
    data-test="quick-filters"
  >
    <ul v-if="quickFilterItems">
      <li
        v-for="(item, index) in quickFilterItems"
        :key="index"
      >
        <button
          class="quick-filter"
          :class="[{ active: Boolean(item.value) }]"
          :aria-label="$t(item.id)"
          :data-test="`quick-filter-${item.id}`"
          data-filter-button-ignored-el
          @click="applyFacet(props.quickFiltersKey, item.id)"
        >
          {{ $t(item.id) }}
        </button>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.quick-filters {
  @import '~/assets/scss/typography/body';

  max-width: 100%;
  height:100%;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ul {
    height: 100%;
    display: flex;
    gap: 4rem;
    flex-wrap: nowrap;
    align-items: center;
    margin: 0;
    list-style: none;
    width: fit-content;
  }

  li {
    &:first-child {
      margin-left: 1.6rem;
    }
    &:last-child {
      margin-right: 1.6rem;
    }
  }

  button.quick-filter {
    @include body2;
    height: 2.5rem;
    white-space: nowrap;

    &.active {
      background-image: url('/icons/wavyLineBlue.svg');
      position: relative;
      background-repeat: repeat-x;
      background-position-y: bottom;
    }

    &:hover {
      background-image: url('/icons/wavyLine.svg');
      position: relative;
      background-size: 8rem;
      background-repeat: repeat-x;
      background-position-y: bottom;
    }
  }
}
</style>
